import { useId, type ReactNode } from 'react'
import { Field, Label, Textarea as DsTextarea, ValidationMessage } from '@digdir/designsystemet-react'
import { HelpText } from './HelpText'

// ---------------------------------------------------------------------------
// Textarea — presentational form field (props in → UI out).
// Designsystemet owns appearance and field wiring; classes retain layout hooks.
// No data fetching, no privileged logic, no surface assumptions.
// Safe for the public, sessionless register bundle.
// ---------------------------------------------------------------------------

type TextareaProps = {
  /** Stable control ID for links from an error summary; generated when omitted. */
  id?: string
  /** Visible label text (already i18n'd by the caller). */
  label: string
  labelActions?: ReactNode
  /** Controlled value. */
  value: string
  /** Called on every change with the React ChangeEvent. */
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  /** Number of visible text rows. Defaults to 4. */
  rows?: number
  readOnly?: boolean
  disabled?: boolean
  required?: boolean
  maxLength?: number
  /** Optional hint text rendered below the label. */
  hint?: string
  help?: string
  placeholder?: string
  /** Error message. When present, marks the field invalid. */
  error?: string
}

/**
 * Controlled textarea with a label, optional hint, and optional error state.
 *
 * Usage:
 *   <Textarea
 *     label={t('form.notes')}
 *     value={notes}
 *     onChange={e => setNotes(e.target.value)}
 *     rows={6}
 *     hint={t('form.notes_hint')}
 *     error={errors.notes}
 *   />
 */
export function Textarea({ id: providedId, label, labelActions, value, onChange, rows = 4, hint, help, placeholder, error, readOnly, disabled, required, maxLength }: TextareaProps) {
  const generatedId = useId()
  const id = providedId ?? generatedId
  const hasError = Boolean(error)
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-err` : undefined

  return (
    <Field className="field">
      <div className="field__heading"><Label htmlFor={id} className="field__lbl">
        {label}
      </Label>{help && <HelpText label={label} text={help} />}{labelActions}</div>

      {hint && (
        <Field.Description id={hintId} className="field__hint">{hint}</Field.Description>
      )}

      <DsTextarea
        id={id}
        className="textarea"
        value={value}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
        aria-invalid={hasError ? 'true' : undefined}
      />

      {error && (
        <ValidationMessage id={errorId} className="field__err" role="alert">
          {error}
        </ValidationMessage>
      )}
    </Field>
  )
}
